/**
 * THE PANEL, FROM MD UP. The row you pressed, and everything you can do to it, beside the list
 * from xl and under it below xl (ops #56), never over it. Her texts on the enquiry come first among
 * them (ops #26, part 2), read as the panel opens, since it is one row's and one cheap read.
 */
import { useState } from 'react';
import { SERVICE_LINE_LABELS } from '@fk/shared';
import {
  discardEnquiry,
  type Discard,
  type EnquiryRow,
  type Me,
  type OfficeUser,
} from '../../api.ts';
import { Detail, Pill } from '../../components/AppShell.tsx';
import { CallLink, MailLink } from '../../components/CallLink.tsx';
import { MarkPills } from '../../components/DoNotContact.tsx';
import { OpenTheCall } from '../../components/OpenTheCall.tsx';
import { DiscardControl } from '../../components/Discard.tsx';
import { Button } from '../../components/Field.tsx';
import { PanelRule, SidePanel } from '../../components/ListView.tsx';
import { GapPill } from '../../components/StatusPills.tsx';
import { formatInstant } from '../../format.ts';
import { CHANNEL_LABELS, ENQUIRY_STATE_LABELS } from '../../labels.ts';
import { openEnquiry } from '../../source.ts';
import { TextThread } from '../TextThread.tsx';
import { MakeClientForm } from './MakeClientForm.tsx';
import { NextAction, Owner, SheIsAHousehold } from './RowWords.tsx';
import { WhereSheCameFrom, sourceKey } from './WhereSheCameFrom.tsx';
import { WorkItForm } from './WorkItForm.tsx';
import { FollowUps } from './FollowUps.tsx';
import { becameAHousehold, heldBackWords, stateTone } from './rows.ts';

export function WorkPanel({
  row,
  today,
  me,
  people,
  onChanged,
  onClose,
}: {
  row: EnquiryRow;
  today: string;
  me: Me;
  people: readonly OfficeUser[];
  onChanged: (sentence: string) => void;
  onClose: () => void;
}) {
  const [converting, setConverting] = useState(false);
  const open = openEnquiry(row.state);
  const noReply = row.state === 'new' && row.firstResponseAt === null;
  const overdue = open && row.nextActionOn !== null && row.nextActionOn < today;

  return (
    <SidePanel
      title={row.name}
      onClose={onClose}
      pills={
        <>
          <Pill tone={stateTone(row.state)}>{ENQUIRY_STATE_LABELS[row.state]}</Pill>
          {noReply ? <GapPill>No reply yet</GapPill> : null}
          <MarkPills marks={row.doNotContact} />
        </>
      }
    >
      <p className="text-sm text-navy/75">
        {CHANNEL_LABELS[row.sourceChannel]}
        {row.serviceLine ? `, ${SERVICE_LINE_LABELS[row.serviceLine].toLowerCase()}` : ''}
        {row.sourceDetail ? (
          <span className="ml-2 text-xs text-navy/65">{row.sourceDetail}</span>
        ) : null}
      </p>

      {row.phoneE164 || row.email ? (
        <div className="flex flex-wrap gap-2">
          {row.phoneE164 ? (
            <CallLink phoneE164={row.phoneE164} marked={row.doNotContact?.call ?? null} />
          ) : null}
          <MailLink email={row.email} />
        </div>
      ) : null}
      {/* The call that opened it, as the phone system tells it (ops #13). */}
      <OpenTheCall me={me} callSid={row.callSid} />

      <div className="space-y-1 text-sm">
        <Owner row={row} me={me} open={open} />
        <NextAction row={row} open={open} overdue={overdue} />
      </div>

      <dl>
        <Detail label="Arrived" value={formatInstant(row.createdAt)} />
        <Detail label="First reply" value={formatInstant(row.firstResponseAt)} />
        <Detail label="Held back by" value={heldBackWords(row)} />
        <Detail label="Lost because" value={row.lostReason} />
      </dl>

      {/* The quote's follow-up (ops #86): its steps and what came of each, read as the panel opens. */}
      <FollowUps enquiryId={row.id} />

      {/* Her texts on the enquiry, oldest first (ops #26, part 2), whatever became of it since. */}
      <PanelRule />
      <h3 className="font-serif text-lg leading-7">Texts</h3>
      <TextThread of={{ kind: 'enquiry', id: row.id }} herName={row.name} />

      {becameAHousehold(row) ? (
        <SheIsAHousehold row={row} />
      ) : (
        <>
          <PanelRule />
          <h3 className="font-serif text-lg leading-7">Work it</h3>
          <WorkItForm idPrefix="panel" row={row} me={me} people={people} onChanged={onChanged} />

          <PanelRule />
          <h3 className="font-serif text-lg leading-7">Where they came from</h3>
          <WhereSheCameFrom key={sourceKey(row)} idPrefix="panel" row={row} onChanged={onChanged} />

          <PanelRule />
          <Button onClick={() => setConverting((shown) => !shown)} full>
            Make them a client
          </Button>
          {converting ? (
            <MakeClientForm idPrefix="panel" row={row} />
          ) : (
            <p className="text-xs text-navy/65">
              The household, the person to ring and the home are made in one write, and this is
              marked booked in the same one.
            </p>
          )}

          {/*
           * Last, under everything worth doing first, and quiet. A discard is not a gap somebody
           * has to close, so it is never coral, and it opens into a reason and a confirming press
           * rather than acting on the first tap.
           */}
          <PanelRule />
          {/*
           * And it is the ONE sheet that offers a privacy request, because the enquiry is the one
           * row this product can actually erase: blanked, soft deleted, every audit image of it
           * redacted, and the website told `forget`. The call log, the expense book and the task
           * list draw the same control and the server refuses the word at all three.
           */}
          <DiscardControl
            idPrefix={`enquiry-${row.id}`}
            what="this enquiry"
            doneSentence="Discarded. It is off the list, and why is on the record."
            hint="It leaves the list. The record and the reason stay, and the website is told when it sent this one."
            canForget
            onDiscard={(body: Discard) => discardEnquiry(row.id, body)}
            onDone={onChanged}
          />
        </>
      )}
    </SidePanel>
  );
}
